import { ChevronRight } from '@mui/icons-material';
import React, { useState } from 'react';

import {
  PriorityBadge,
  ContractValueWrapper,
  ContractLogo,
  StatusChip,
  StatusChipLabel,
  StatusChipArrow,
} from './MobileServiceSummary.styles';
import { NextServiceStatusesResponse, ServiceObject } from '../../../../../@types/service.type';
import { isSafeUrl } from '../../../../../utils/url-validation';
import {
  DetailSectionHeader,
  DetailCard,
  DetailRow,
  DetailLabel,
  DetailValue,
} from '../../MobileServiceDetail.styles';
import { MobileStatusUpdateDialog } from '../MobileQuickActions/MobileStatusUpdateDialog';

interface MobileServiceSummaryProps {
  service: ServiceObject;
  logoPic?: string;
  nextStatuses?: NextServiceStatusesResponse;
  onStatusChanged: () => void | Promise<void>;
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'Not scheduled';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Not scheduled';
    return date.toLocaleString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Not scheduled';
  }
};

export const MobileServiceSummary: React.FC<MobileServiceSummaryProps> = ({
  service,
  logoPic,
  nextStatuses,
  onStatusChanged,
}) => {
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);

  return (
    <>
      <MobileStatusUpdateDialog
        open={isStatusDialogOpen}
        onClose={() => setIsStatusDialogOpen(false)}
        service={service}
        nextStatuses={nextStatuses}
        onStatusChanged={onStatusChanged}
      />

      {/* Job Details */}
      <DetailSectionHeader sx={{ marginTop: '12px' }}>Job Details</DetailSectionHeader>
      <DetailCard>
        <DetailRow>
          <DetailLabel>Status</DetailLabel>
          <DetailValue>
            <StatusChip
              onClick={() => setIsStatusDialogOpen(true)}
              onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsStatusDialogOpen(true);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Update status"
            >
              <StatusChipLabel>{service.serviceStatus?.name || '-'}</StatusChipLabel>
              <StatusChipArrow>
                <ChevronRight sx={{ fontSize: '0.9rem' }} />
              </StatusChipArrow>
            </StatusChip>
          </DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Contract</DetailLabel>
          <DetailValue>
            <ContractValueWrapper>
              {logoPic && isSafeUrl(logoPic) && (
                <ContractLogo src={logoPic} alt={service.contract || 'Contract'} />
              )}
              {service.contract || service.contractName || '-'}
            </ContractValueWrapper>
          </DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Brand</DetailLabel>
          <DetailValue>{service.brand || '-'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Priority</DetailLabel>
          <DetailValue>
            <PriorityBadge bgColor={service.priorityColor || service.priority?.color}>
              {service.priorityLabel || service.priority?.shortName || '-'}
            </PriorityBadge>
          </DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Technician</DetailLabel>
          <DetailValue>{service.primaryAllocation?.node?.name || 'Unallocated'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Scheduled</DetailLabel>
          <DetailValue>{formatDate(service.scheduledAt)}</DetailValue>
        </DetailRow>
      </DetailCard>

      {/* Service Classification */}
      <DetailSectionHeader>Service Classification</DetailSectionHeader>
      <DetailCard>
        <DetailRow>
          <DetailLabel>Service Type</DetailLabel>
          <DetailValue>{service.serviceType?.[0]?.name || '-'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Action Type</DetailLabel>
          <DetailValue>{service.actionType?.[0]?.name || '-'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Process Type</DetailLabel>
          <DetailValue>{service.processType?.[0]?.name || '-'}</DetailValue>
        </DetailRow>
        {(service.zone || service.mainLocationZone) && (
          <DetailRow>
            <DetailLabel>Zone</DetailLabel>
            <DetailValue>{service.zone || service.mainLocationZone}</DetailValue>
          </DetailRow>
        )}
        {(service.referenceNo1 || service.referenceNo2 || service.referenceNo3) && (
          <DetailRow>
            <DetailLabel>References</DetailLabel>
            <DetailValue>
              {[service.referenceNo1, service.referenceNo2, service.referenceNo3]
                .filter(Boolean)
                .join(' | ')}
            </DetailValue>
          </DetailRow>
        )}
      </DetailCard>

      {/* Equipment */}
      <DetailSectionHeader>Equipment</DetailSectionHeader>
      <DetailCard>
        <DetailRow>
          <DetailLabel>Model</DetailLabel>
          <DetailValue>{service.reportedModel || '-'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Serial</DetailLabel>
          <DetailValue>{service.reportedSerialNumber || '-'}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>Fault</DetailLabel>
          <DetailValue>{service.reportedFaultAndDescription || '-'}</DetailValue>
        </DetailRow>
      </DetailCard>
    </>
  );
};
